import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import PriceSidebar from './PriceSidebar';
import Stepper from './Stepper';
import { clearErrors } from '../../actions/orderAction';
import { useSnackbar } from 'notistack';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import MetaData from '../Layouts/MetaData';
import { emptyCart, removePromoCode } from '../../actions/cartAction';
import useCartPricing from '../../utils/useCartPricing';
import usePromoDiscount from '../../utils/usePromoDiscount';
import useShippingCharge from '../../utils/useShippingCharge';
import { useLang, uiText } from '../../utils/i18n';

const stripePromise = loadStripe('pk_live_xWzWSyu3bi0iBdbBeqftvUdn');

const elementStyle = {
    style: {
        base: {
            fontSize: '15px',
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
            color: '#1a1a1a',
            '::placeholder': { color: '#9ca3af' },
        },
        invalid: { color: '#d33' },
    },
};

const BrandMark = ({ brand }) => {
    if (brand === 'visa') {
        return <span style={{ fontStyle: 'italic', fontWeight: 800, fontSize: 18, color: '#1a1f71', letterSpacing: 1 }}>VISA</span>;
    }
    if (brand === 'mastercard') {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#EB001B', marginRight: -8 }} />
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#F79E1B', opacity: 0.9 }} />
            </div>
        );
    }
    if (brand === 'amex') {
        return <span style={{ fontWeight: 800, fontSize: 12, color: '#2E77BC', border: '1.5px solid #2E77BC', borderRadius: 4, padding: '2px 6px' }}>AMEX</span>;
    }
    if (brand === 'discover') {
        return <span style={{ fontWeight: 800, fontSize: 13, color: '#ff6000', fontStyle: 'italic' }}>DISCOVER</span>;
    }
    return (
        <svg width="26" height="18" viewBox="0 0 30 22" fill="none">
            <rect x="0.5" y="0.5" width="29" height="21" rx="3" stroke="#9ca3af" />
            <rect x="4" y="6" width="22" height="2.5" rx="1" fill="#9ca3af" />
        </svg>
    );
};

const PaymentInner = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const stripe = useStripe();
    const elements = useElements();
    const { lang } = useLang();

    const [payDisable, setPayDisable] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('cod');
    const [gateways, setGateways] = useState(null);

    useEffect(() => {
        const fetchGateways = async () => {
            try {
                const { data } = await axios.get('/api/v1/payment-gateways');
                setGateways(data.gateways);
            } catch (err) {
                // If this fails for any reason, fall back to just COD + Card
                // so checkout never breaks entirely.
                setGateways({
                    cod: { website: 'enabled' }, card: { website: 'enabled' },
                    alipay: { website: 'disabled' }, wechatPay: { website: 'disabled' }, octopus: { website: 'disabled' },
                });
            }
        };
        fetchGateways();
    }, []);

    useEffect(() => {
        if (!gateways) return;
        const keyFor = { cod: 'cod', card: 'card', alipay: 'alipay', wechat_pay: 'wechatPay' };
        if (gateways[keyFor[paymentMethod]]?.website !== 'enabled') {
            const firstEnabled = ['cod', 'card', 'alipay', 'wechat_pay'].find((m) => gateways[keyFor[m]]?.website === 'enabled');
            if (firstEnabled) setPaymentMethod(firstEnabled);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gateways]);
    const [cardBrand, setCardBrand] = useState(null);

    const { shippingInfo, cartItems, promoCode } = useSelector((state) => state.cart);
    const { user } = useSelector((state) => state.user);
    const { error } = useSelector((state) => state.newOrder);
    const pricing = useCartPricing(cartItems);
    const promoResult = usePromoDiscount(promoCode, pricing?.total);

    // Only used for display and as the base for the payment amount — the
    // actual order total is always recalculated authoritatively on the
    // backend from real product prices, offers, promo code, AND shipping,
    // never trusted from here.
    const afterOfferTotal = pricing ? pricing.total : cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const promoDiscount = promoResult?.valid ? promoResult.discountAmount : 0;
    const afterPromoTotal = Math.max(afterOfferTotal - promoDiscount, 0);

    // Shipping MUST be included here too — this is what actually gets
    // charged via Stripe and submitted as the order total, and it has to
    // match what the backend independently recalculates, or a genuine
    // order (with shipping added) will always fail its payment-amount
    // verification even though the customer was already charged.
    const { charge: shippingCharge } = useShippingCharge(afterPromoTotal);
    const totalPrice = afterPromoTotal + shippingCharge;

    // --- 1313 Wallet redemption ---
    const [wallet, setWallet] = useState(null);
    const [walletSettings, setWalletSettings] = useState(null);
    const [pointsToRedeem, setPointsToRedeem] = useState(0);
    const [walletCashToUse, setWalletCashToUse] = useState(0);

    useEffect(() => {
        axios.get('/api/v1/my-wallet').then(({ data }) => setWallet(data)).catch(() => {});
        axios.get('/api/v1/wallet-settings').then(({ data }) => setWalletSettings(data.settings)).catch(() => {});
    }, []);

    const pointsValueHKD = walletSettings ? Number((pointsToRedeem / walletSettings.pointsToCurrencyRedemption).toFixed(2)) : 0;
    const walletValueRequested = Number((pointsValueHKD + (Number(walletCashToUse) || 0)).toFixed(2));
    const maxWalletAllowed = walletSettings ? Number((totalPrice * (walletSettings.maxRedemptionPercent / 100)).toFixed(2)) : 0;
    const walletValueApplied = Math.min(walletValueRequested, maxWalletAllowed, totalPrice);
    const finalPayable = Number(Math.max(totalPrice - walletValueApplied, 0).toFixed(2));
    const fullyCoveredByWallet = finalPayable === 0 && walletValueApplied > 0;

    const submitCodHandler = async () => {
        const { data: paymentData } = await axios.post(
            '/api/v1/payment/process',
            { amount: Math.round(finalPayable), email: user.email, phoneNo: shippingInfo.phoneNo },
            { headers: { "Content-Type": "application/json" } },
        );
        return { paymentInfo: { id: paymentData.paymentId, status: 'PENDING' }, method: 'cod' };
    };

    const submitCardHandler = async () => {
        if (!stripe || !elements) {
            throw new Error(uiText('paymentFormLoading', lang));
        }

        const { data: intentData } = await axios.post('/api/v1/payment/create-intent', {
            orderItems: cartItems,
            promoCode: promoResult?.valid ? promoCode : undefined,
            shippingInfo,
            pointsToRedeem,
            walletCashToUse,
            platform: 'website',
        });

        const { paymentIntent, error: stripeError } = await stripe.confirmCardPayment(intentData.clientSecret, {
            payment_method: {
                card: elements.getElement(CardNumberElement),
                billing_details: { name: user.name, email: user.email },
            },
        });

        if (stripeError) {
            throw new Error(stripeError.message);
        }
        if (paymentIntent.status !== 'succeeded') {
            throw new Error(uiText('paymentNotCompleted', lang));
        }

        return { paymentInfo: { id: paymentIntent.id, status: paymentIntent.status }, method: 'card' };
    };

    // Alipay is a redirect-based payment method — the browser fully leaves
    // this page, goes to Alipay, then comes back to a separate return page.
    // Since that reload wipes out all in-memory state (cart, address,
    // promo code), we stash everything needed to finish the order in
    // sessionStorage first, and the return page picks it up from there.
    const submitAlipayHandler = async () => {
        if (!stripe) {
            throw new Error(uiText('paymentFormLoading', lang));
        }

        const { data: intentData } = await axios.post('/api/v1/payment/create-intent', {
            orderItems: cartItems,
            promoCode: promoResult?.valid ? promoCode : undefined,
            shippingInfo,
            paymentMethodType: 'alipay',
            pointsToRedeem,
            walletCashToUse,
            platform: 'website',
        });

        sessionStorage.setItem('alipayPendingOrder', JSON.stringify({
            shippingInfo,
            orderItems: cartItems,
            totalPrice,
            promoCode: promoResult?.valid ? promoCode : undefined,
            pointsToRedeem,
            walletCashToUse,
        }));

        const { error: stripeError } = await stripe.confirmAlipayPayment(intentData.clientSecret, {
            return_url: `${window.location.origin}/order/alipay-return`,
        });

        // If we ever reach this line, the redirect to Alipay itself failed
        // to even start — a genuine payment failure instead redirects back
        // to return_url, it doesn't resolve here.
        if (stripeError) {
            sessionStorage.removeItem('alipayPendingOrder');
            throw new Error(stripeError.message);
        }
    };

    // WeChat Pay doesn't leave the page — Stripe.js shows a QR code overlay
    // right here and resolves once the customer scans and approves it (or
    // cancels), so this plugs into the normal same-page flow like card does.
    const submitWechatPayHandler = async () => {
        const { data: intentData } = await axios.post('/api/v1/payment/create-intent', {
            orderItems: cartItems,
            promoCode: promoResult?.valid ? promoCode : undefined,
            shippingInfo,
            paymentMethodType: 'wechat_pay',
            pointsToRedeem,
            walletCashToUse,
            platform: 'website',
        });

        const result = await stripe.confirmWechatPayPayment(intentData.clientSecret, {
            payment_method_options: {
                wechat_pay: { client: 'web' },
            },
        });

        if (result.error) {
            throw new Error(result.error.message);
        }

        return { paymentInfo: { id: result.paymentIntent.id, status: result.paymentIntent.status }, method: 'wechat_pay' };
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!shippingInfo?.phoneNo) {
            enqueueSnackbar(uiText('missingPhoneNote', lang), { variant: "error", autoHideDuration: 8000 });
            return;
        }

        setPayDisable(true);

        // If points + wallet cash fully cover the order, skip any payment
        // method entirely — the backend records this as paymentMethod
        // 'wallet' automatically.
        if (fullyCoveredByWallet) {
            try {
                const { data: orderData } = await axios.post('/api/v1/order/new', {
                    shippingInfo,
                    orderItems: cartItems,
                    totalPrice,
                    promoCode: promoResult?.valid ? promoCode : undefined,
                    pointsToRedeem,
                    walletCashToUse,
                    orderSource: 'website',
                });

                dispatch(emptyCart());
                dispatch(removePromoCode());

                enqueueSnackbar(uiText('orderPlaced', lang), { variant: "success" });
                navigate("/orders/success", { state: { orderNumber: orderData.order.orderNumber } });
            } catch (err) {
                setPayDisable(false);
                enqueueSnackbar(err?.response?.data?.message || err?.message || uiText('somethingWentWrong', lang), { variant: "error", autoHideDuration: 10000 });
            }
            return;
        }

        if (paymentMethod === 'alipay') {
            try {
                await submitAlipayHandler();
                // On success, the line above navigates the browser away to
                // Alipay and never returns here — this only re-enables the
                // button if starting the redirect itself failed.
            } catch (err) {
                setPayDisable(false);
                enqueueSnackbar(err?.response?.data?.message || err?.message || uiText('somethingWentWrong', lang), { variant: "error", autoHideDuration: 10000 });
            }
            return;
        }

        try {
            // Step 1: take payment — COD stub, card, or WeChat Pay. (Alipay
            // is handled separately above, since it redirects off this page
            // entirely rather than resolving here.)
            let paymentInfo, method;
            if (paymentMethod === 'card') {
                ({ paymentInfo, method } = await submitCardHandler());
            } else if (paymentMethod === 'wechat_pay') {
                ({ paymentInfo, method } = await submitWechatPayHandler());
            } else {
                ({ paymentInfo, method } = await submitCodHandler());
            }

            // Step 2: create the order — awaited directly (not via the
            // fire-and-forget Redux thunk) so a rejection here is actually
            // caught below, instead of silently showing "success" and
            // clearing the cart for an order that was never created.
            const { data: orderData } = await axios.post('/api/v1/order/new', {
                shippingInfo,
                orderItems: cartItems,
                totalPrice,
                promoCode: promoResult?.valid ? promoCode : undefined,
                paymentInfo,
                paymentMethod: method,
                pointsToRedeem,
                walletCashToUse,
                orderSource: 'website',
            });

            dispatch(emptyCart());
            dispatch(removePromoCode());

            enqueueSnackbar(uiText('orderPlaced', lang), { variant: "success" });
            navigate("/orders/success", { state: { orderNumber: orderData.order.orderNumber } });

        } catch (err) {
            setPayDisable(false);
            const message = err?.response?.data?.message || err?.message || uiText('somethingWentWrong', lang);
            enqueueSnackbar(
                paymentMethod === 'card'
                    ? `${uiText('cardChargedOrderFailed', lang)} ${message}. ${uiText('contactSupportNote', lang)}`
                    : message,
                { variant: "error", autoHideDuration: 10000 }
            );
        }
    };

    useEffect(() => {
        if (error) {
            dispatch(clearErrors());
            enqueueSnackbar(error, { variant: "error" });
        }
    }, [dispatch, error, enqueueSnackbar]);


    return (
        <>
            <MetaData title="Secure Payment" />

            <main className="w-full mt-20">

                <div className="flex flex-col sm:flex-row gap-3.5 w-full sm:w-11/12 mt-0 sm:mt-4 m-auto sm:mb-7">

                    <div className="flex-1">

                        <Stepper activeStep={3}>
                            <div className="w-full bg-white">

                                <form onSubmit={(e) => submitHandler(e)} autoComplete="off" className="flex flex-col justify-start gap-2 w-full mx-8 my-4 overflow-hidden">
                                    <FormControl>
                                        <RadioGroup
                                            aria-labelledby="payment-radio-group"
                                            name="payment-radio-button"
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                        >
                                            {gateways?.cod?.website !== 'disabled' && (
                                                <FormControlLabel
                                                    value="cod"
                                                    control={<Radio />}
                                                    disabled={gateways?.cod?.website === 'comingSoon'}
                                                    label={`${uiText('cashOnDelivery', lang)}${gateways?.cod?.website === 'comingSoon' ? ' ' + uiText('comingSoonSuffix', lang) : ''}`}
                                                />
                                            )}
                                            {gateways?.card?.website !== 'disabled' && (
                                                <FormControlLabel
                                                    value="card"
                                                    control={<Radio />}
                                                    disabled={gateways?.card?.website === 'comingSoon'}
                                                    label={`${uiText('creditDebitCard', lang)}${gateways?.card?.website === 'comingSoon' ? ' ' + uiText('comingSoonSuffix', lang) : ''}`}
                                                />
                                            )}
                                            {gateways?.alipay?.website !== 'disabled' && (
                                                <FormControlLabel
                                                    value="alipay"
                                                    control={<Radio />}
                                                    disabled={gateways?.alipay?.website === 'comingSoon'}
                                                    label={`Alipay${gateways?.alipay?.website === 'comingSoon' ? ' ' + uiText('comingSoonSuffix', lang) : ''}`}
                                                />
                                            )}
                                            {gateways?.wechatPay?.website !== 'disabled' && (
                                                <FormControlLabel
                                                    value="wechat_pay"
                                                    control={<Radio />}
                                                    disabled={gateways?.wechatPay?.website === 'comingSoon'}
                                                    label={`WeChat Pay${gateways?.wechatPay?.website === 'comingSoon' ? ' ' + uiText('comingSoonSuffix', lang) : ''}`}
                                                />
                                            )}
                                        </RadioGroup>
                                    </FormControl>

                                    {paymentMethod === 'alipay' && (
                                        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-4 py-3 my-2 max-w-md">
                                            {uiText('alipayRedirectNote', lang)}
                                        </p>
                                    )}

                                    {paymentMethod === 'wechat_pay' && (
                                        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-4 py-3 my-2 max-w-md">
                                            {uiText('wechatPayQrNote', lang)}
                                        </p>
                                    )}

                                    {wallet && walletSettings?.enabled && (wallet.points > 0 || wallet.cash > 0) && (
                                        <div className="rounded-lg border border-gray-200 p-4 my-2 max-w-md bg-orange-50">
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-sm font-medium text-gray-700">{uiText('payWithWallet', lang)}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/my-wallet')}
                                                    className="text-xs text-primary-blue font-medium hover:underline"
                                                >
                                                    {uiText('loadMoney', lang)}
                                                </button>
                                            </div>
                                            <div className="flex gap-3">
                                                <div className="flex-1">
                                                    <label className="block text-xs text-gray-500 mb-1">{uiText('pointsHave', lang)} {wallet.points.toLocaleString()}{uiText('closeParen', lang)}</label>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={wallet.points}
                                                        value={pointsToRedeem}
                                                        onChange={(e) => setPointsToRedeem(Math.max(0, Math.min(wallet.points, Number(e.target.value) || 0)))}
                                                        className="border border-gray-300 rounded px-3 py-2 text-sm w-full"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="block text-xs text-gray-500 mb-1">{uiText('cashHave', lang)} HK${wallet.cash.toLocaleString()}{uiText('closeParen', lang)}</label>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={wallet.cash}
                                                        value={walletCashToUse}
                                                        onChange={(e) => setWalletCashToUse(Math.max(0, Math.min(wallet.cash, Number(e.target.value) || 0)))}
                                                        className="border border-gray-300 rounded px-3 py-2 text-sm w-full"
                                                    />
                                                </div>
                                            </div>
                                            {walletValueApplied > 0 && (
                                                <p className="text-xs text-gray-600 mt-2">
                                                    {uiText('applyingFromWallet', lang)} HK${walletValueApplied.toLocaleString()} {uiText('fromYourWalletYoullPay', lang)} HK${finalPayable.toLocaleString()}{fullyCoveredByWallet ? ' ' + uiText('fullyCoveredNote', lang) : ''}.
                                                </p>
                                            )}
                                            {walletSettings && (
                                                <p className="text-xs text-gray-400 mt-1">{uiText('walletMaxNote1', lang)} {walletSettings.maxRedemptionPercent}{uiText('walletMaxNote2', lang)}{maxWalletAllowed.toLocaleString()}{uiText('walletMaxNote3', lang)}</p>
                                            )}
                                        </div>
                                    )}

                                    {!fullyCoveredByWallet && paymentMethod === 'card' && (
                                        <div className="rounded-lg border border-gray-200 p-5 my-2 max-w-md bg-white shadow-sm">
                                            <div className="flex items-center justify-between mb-4">
                                                <p className="text-sm font-medium text-gray-700">{uiText('paySecurelyWithCard', lang)}</p>
                                                <span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, fontSize: 20, color: '#635BFF' }}>stripe</span>
                                            </div>

                                            <div className="flex flex-col gap-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">{uiText('cardNumberLabel', lang)}</label>
                                                    <div className="border border-gray-300 rounded-md px-3 py-2.5 flex items-center justify-between focus-within:border-primary-blue">
                                                        <div className="flex-1">
                                                            <CardNumberElement
                                                                options={{ ...elementStyle, placeholder: '1234 1234 1234 1234' }}
                                                                onChange={(e) => setCardBrand(e.brand !== 'unknown' ? e.brand : null)}
                                                            />
                                                        </div>
                                                        <BrandMark brand={cardBrand} />
                                                    </div>
                                                </div>

                                                <div className="flex gap-3">
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">{uiText('expiryDateLabel', lang)}</label>
                                                        <div className="border border-gray-300 rounded-md px-3 py-2.5 focus-within:border-primary-blue">
                                                            <CardExpiryElement options={elementStyle} />
                                                        </div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">{uiText('cvvLabel', lang)}</label>
                                                        <div className="border border-gray-300 rounded-md px-3 py-2.5 focus-within:border-primary-blue">
                                                            <CardCvcElement options={elementStyle} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <p className="text-xs text-gray-400 mt-3">🔒 {uiText('stripeSecureNote', lang)}</p>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between w-full my-2">
                                        <button type="button" onClick={() => navigate('/order/confirm')} className="text-sm font-medium text-gray-600 hover:text-primary-blue flex items-center gap-1">
                                            &larr; {uiText('backWord', lang)}
                                        </button>
                                        <input type="submit" value={`${uiText('placeOrderAmount', lang)}${finalPayable.toLocaleString()}`} disabled={payDisable ? true : false} className={`${payDisable ? "bg-primary-grey cursor-not-allowed" : "bg-primary-orange cursor-pointer"} py-3 px-8 font-medium text-white shadow hover:shadow-lg rounded-sm uppercase outline-none`} />
                                    </div>

                                </form>

                            </div>
                        </Stepper>
                    </div>

                    <PriceSidebar cartItems={cartItems} pricing={pricing} walletValueApplied={walletValueApplied} />
                </div>
            </main>
        </>
    );
};

const Payment = () => (
    <Elements stripe={stripePromise}>
        <PaymentInner />
    </Elements>
);

export default Payment;